import { Trans } from "@lingui/macro";

/** Shared question + hint heading used by every card type */
export function CardQuestion({
  question,
  hint,
  index: _index,
}: {
  question: string;
  hint?: string;
  index?: number;
}) {
  return (
    <div className="mb-6 sm:mb-8">
      <h2 className="text-[clamp(22px,5vw,36px)] font-bold text-gray-900 leading-tight">
        {question}
      </h2>
      {hint && (
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">{hint}</p>
      )}
    </div>
  );
}

/** The primary "Continue" button */
export function OkButton({
  onClick,
  label,
  disabled = false,
  loading = false,
}: {
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className="ok-btn box-border appearance-none flex h-12 w-full md:w-auto justify-center items-center gap-2 px-6 rounded-lg bg-[#166B93] text-white font-semibold text-sm leading-none shadow-lg shadow-[#166B93]/20 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? (
        <>
          <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          <Trans>Please wait…</Trans>
        </>
      ) : (
        <>
          {label ?? <Trans>Continue</Trans>}
          <span className="text-white/70 text-xs font-normal">↵</span>
        </>
      )}
    </button>
  );
}

/** Back button — subtle ghost style */
export function BackButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="back-btn box-border appearance-none flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-lg px-3.5 font-medium text-sm leading-none transition-all duration-150 cursor-pointer relative z-10 max-md:border max-md:border-gray-200 max-md:bg-white max-md:text-gray-800 max-md:shadow-sm max-md:hover:bg-gray-50 max-md:active:bg-gray-100 md:border-0 md:bg-transparent md:px-4 md:text-gray-600 md:shadow-none md:hover:text-gray-900 md:hover:bg-gray-100/90 md:active:bg-gray-200/90"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
      {label ?? <Trans>Back</Trans>}
    </button>
  );
}

/** Skip button — mirrors BackButton (outlined on mobile) with forward chevron */
export function SkipButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="skip-btn box-border appearance-none flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-lg px-3.5 font-medium text-sm leading-none transition-all duration-150 cursor-pointer relative z-10 max-md:border max-md:border-gray-200 max-md:bg-white max-md:text-gray-800 max-md:shadow-sm max-md:hover:bg-gray-50 max-md:active:bg-gray-100 md:border-0 md:bg-transparent md:px-4 md:text-gray-600 md:shadow-none md:hover:text-gray-900 md:hover:bg-gray-100/90 md:active:bg-gray-200/90"
    >
      {label ?? <Trans>Skip</Trans>}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}

/** Row of action buttons: Back (left) + Continue (center/flex) + optional Skip (right) */
export function CardActions({
  onNext,
  onBack,
  onSkip,
  nextLabel,
  nextLoading = false,
  nextDisabled = false,
  showBack = true,
  /** When false, hides Continue (e.g. auto-advance choice cards that only show Back + Skip on desktop). */
  showContinue = true,
  extraAction,
}: {
  onNext: () => void;
  onBack?: () => void;
  /** Optional step: shown on the right, same row as Continue (mirrors Back on the left). */
  onSkip?: () => void;
  nextLabel?: string;
  nextLoading?: boolean;
  nextDisabled?: boolean;
  showBack?: boolean;
  showContinue?: boolean;
  extraAction?: React.ReactNode;
}) {
  return (
    <div className="card-actions-row mt-6 hidden max-w-lg flex-row flex-nowrap items-center gap-1.5 sm:gap-3 md:flex [&>button]:!m-0">
      {showBack && onBack && <BackButton onClick={onBack} />}
      {showContinue ? (
        <div className="min-w-0 flex-1 md:flex-initial md:w-auto md:min-w-0 [&>button]:!m-0">
          <OkButton
            onClick={onNext}
            label={nextLabel}
            loading={nextLoading}
            disabled={nextDisabled}
          />
        </div>
      ) : (
        onSkip && <div className="min-w-0 flex-1" aria-hidden />
      )}
      {onSkip && <SkipButton onClick={onSkip} />}
      {extraAction ? (
        <div className="flex basis-full justify-center md:basis-auto md:flex-initial md:justify-start">
          {extraAction}
        </div>
      ) : null}
    </div>
  );
}

/** Thin "press Enter" hint below buttons */
export function PressEnterHint() {
  return (
    <p className="mt-3 hidden text-xs text-gray-400 md:block">
      <Trans>
        Press <kbd className="font-semibold">Enter ↵</kbd> to continue
      </Trans>
    </p>
  );
}
